/*
 * CTI.PrimaryCategoryRedirect
 *
 * A Commerce Category can be reached by more than one URL when it is added as a
 * subcategory of more than one parent. Example (Enclosures, internal id 172):
 *   /shop-by-category/Enclosures       -> Primary = Yes
 *   /Shop_by_Brand/apex9/Enclosures    -> Primary = No (secondary)
 *
 * This extension always sends the visitor to the URL marked Primary = Yes:
 *   1. Menu clicks: links in the header menus are checked against the Commerce
 *      Categories API when the visitor hovers/touches them. If the link is a
 *      secondary URL, the click goes straight to the primary URL.
 *   2. Safety net: after any category page renders, if it was opened through a
 *      secondary URL, the address is replaced with the primary URL.
 *
 * Direct visits / reloads / search engines are handled by the NetSuite redirect
 * (Commerce > Hosting > Redirects). Both should stay in place.
 *
 * No category names are hard-coded: the primary URL comes from the category's
 * "isprimaryurl" and "canonical" values returned by NetSuite.
 */
define('CTI.PrimaryCategoryRedirect', [
	'Backbone',
	'jQuery',
	'underscore'
], function (
	Backbone,
	jQuery,
	_
) {
	'use strict';

	var API_PATH = '/api/navigation/v1/categorynavitems';

	// Menu areas whose links are checked before the click (desktop + mobile menu)
	var MENU_SELECTOR = '.header-menu-secondary-nav, .header-sidebar-menu, .header-menu-level-container';

	// { "/Shop_by_Brand/apex9/Enclosures": "/shop-by-category/Enclosures" | null }
	// null = checked, already primary (or not a category) -> no redirect
	var cache = {};
	var pending = {};

	// "/Shop_by_Brand/apex9/Enclosures/?x=1" -> "/Shop_by_Brand/apex9/Enclosures"
	// Case is kept: NetSuite category URLs are case-sensitive.
	function cleanPath(url) {
		var path = String(url || '').replace(/^#!?/, '').replace(/[?#].*$/, '');
		path = ('/' + path).replace(/\/+/g, '/');
		return path.length > 1 ? path.replace(/\/$/, '') : path;
	}

	function apiParams(path) {
		var env = (window.SC && SC.ENVIRONMENT) || {};
		var locale = (env.currentLanguage && env.currentLanguage.locale) || 'en_US';
		return {
			c: env.companyId,
			n: env.siteSettings && env.siteSettings.siteid,
			site_id: env.siteSettings && env.siteSettings.siteid,
			language: locale.split('_')[0],
			country: locale.split('_')[1] || 'US',
			currency: (env.currentCurrency && env.currentCurrency.code) || 'USD',
			use_pcv: 'T',
			category_fields: 'internalid,fullurl,isprimaryurl',
			full_url: path
		};
	}

	// Returns the primary URL for a secondary category URL, or null if no redirect is needed
	function targetFromCategory(category, path) {
		if (!category || category.isprimaryurl !== false || !category.canonical) {
			return null;
		}
		var target = cleanPath(category.canonical);
		return target !== path ? target : null;
	}

	// Looks up a path once; results are cached for the session
	function resolve(path) {
		if (_.has(cache, path)) {
			return jQuery.Deferred().resolve(cache[path]).promise();
		}
		if (!pending[path]) {
			pending[path] = jQuery.ajax({ url: API_PATH, data: apiParams(path), dataType: 'json' })
				.then(function (response) {
					var category = response && response.data && response.data[0];
					cache[path] = targetFromCategory(category, path);
					return cache[path];
				}, function () {
					// API error: do nothing, let the normal navigation happen
					cache[path] = null;
					return jQuery.Deferred().resolve(null).promise();
				})
				.always(function () {
					delete pending[path];
				});
		}
		return pending[path];
	}

	function goTo(target, replace) {
		var fragment = target.replace(/^\//, '') + (window.location.search || '');
		Backbone.history.navigate(fragment, { trigger: true, replace: !!replace });
	}

	// Path a menu link points to (SuiteCommerce links carry data-hashtag and href)
	function linkPath(link) {
		var hashtag = link.getAttribute('data-hashtag');
		if (hashtag) {
			return cleanPath(hashtag);
		}
		if (link.hostname && link.hostname !== window.location.hostname) {
			return null;
		}
		return link.pathname ? cleanPath(link.pathname) : null;
	}

	function findMenuLink(element) {
		var link = element && element.closest ? element.closest('a[href]') : null;
		return link && link.closest(MENU_SELECTOR) ? link : null;
	}

	// 1a. Check menu links before they are clicked (hover on desktop, touch on mobile)
	function preload(event) {
		var link = findMenuLink(event.target);
		var path = link && linkPath(link);
		if (path && path !== '/') {
			resolve(path);
		}
	}

	// 1b. On click, if the link is a known secondary URL, go to the primary URL instead.
	//     Runs in the capture phase so it happens before SuiteCommerce's own link handler.
	function onClick(event) {
		if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
			return; // keep "open in new tab" etc. working; the NetSuite redirect covers those
		}
		var link = findMenuLink(event.target);
		var path = link && linkPath(link);
		var target = path && cache[path];
		if (target) {
			event.preventDefault();
			event.stopPropagation();
			goTo(target, false);
		}
	}

	// 2. Safety net: a category page was opened through a secondary URL
	function checkCurrentPage(container) {
		var plp = container.getComponent('PLP');
		var category = plp && plp.getCategoryInfo && plp.getCategoryInfo();
		if (!category) {
			return; // not a category page
		}
		var path = cleanPath(window.location.pathname);
		if (category.isprimaryurl === true) {
			cache[path] = null;
			return;
		}
		var known = typeof category.isprimaryurl === 'boolean' && category.canonical;
		var lookup = known
			? jQuery.Deferred().resolve(targetFromCategory(category, path)).promise()
			: resolve(path);

		lookup.done(function (target) {
			// Only redirect if the visitor is still on the same page
			if (target && cleanPath(window.location.pathname) === path) {
				cache[path] = target;
				goTo(target, true);
			}
		});
	}

	return {
		mountToApp: function mountToApp(container) {
			document.addEventListener('mouseover', preload, true);
			document.addEventListener('touchstart', preload, true);
			document.addEventListener('focusin', preload, true);
			document.addEventListener('click', onClick, true);

			var layout = container.getComponent('Layout');
			if (layout) {
				layout.on('afterShowContent', function () {
					checkCurrentPage(container);
				});
			}
		}
	};
});
